import React, { forwardRef } from 'react';
import { Box, chakra, useStyles } from '@chakra-ui/react';
import {
  ArrowLeft, ArrowRight, ArrowDown, Archive, ArchiveRestore, ChartColumn,
  Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsLeft,
  ChevronsRight, Circle, CircleAlert, CircleCheck, CircleDot, CircleX,
  ClipboardList, Columns3, Download, Eye, EyeOff, Filter, Info, ListChecks, Lock,
  Mail, Menu, Minus, Pencil, Plus, RefreshCw, Search, Settings, Shapes,
  ShieldCheck, SlidersHorizontal, Square, SquareCheck, SquareMinus,
  Terminal, Trash2, TriangleAlert, Users, Wrench, X, Icon,
} from 'lucide-react';
import { coffeemaker } from '@lucide/lab';

// Staff icons share Lucide's 24-unit drawing grid and 2px stroke. Chakra's
// adapter preserves existing color/spacing props and refs across both UI kits.
const adminIcon = (Component, name) => {
  const StyledIcon = chakra(Component);
  const AdminIcon = forwardRef(({ fontSize, boxSize = fontSize === 'small' ? 4 : 5, ...props }, ref) => (
    <StyledIcon
      ref={ref}
      boxSize={boxSize}
      flexShrink={0}
      verticalAlign="middle"
      strokeWidth={2}
      aria-hidden={props['aria-label'] ? undefined : true}
      focusable="false"
      {...props}
      fill="none"
    />
  ));
  AdminIcon.displayName = name;
  return AdminIcon;
};

export const ArrowBackIcon = adminIcon(ArrowLeft, 'ArrowBackIcon');
export const ArrowRightIcon = adminIcon(ArrowRight, 'ArrowRightIcon');
export const ArrowDownIcon = adminIcon(ArrowDown, 'ArrowDownIcon');
export const ArchiveIcon = adminIcon(Archive, 'ArchiveIcon');
export const UnarchiveIcon = adminIcon(ArchiveRestore, 'UnarchiveIcon');
export const ChartIcon = adminIcon(ChartColumn, 'ChartIcon');
export const CheckIcon = adminIcon(Check, 'CheckIcon');
export const ChevronDownIcon = adminIcon(ChevronDown, 'ChevronDownIcon');
export const ChevronLeftIcon = adminIcon(ChevronLeft, 'ChevronLeftIcon');
export const ChevronRightIcon = adminIcon(ChevronRight, 'ChevronRightIcon');
export const ChevronUpIcon = adminIcon(ChevronUp, 'ChevronUpIcon');
export const FirstPageIcon = adminIcon(ChevronsLeft, 'FirstPageIcon');
export const LastPageIcon = adminIcon(ChevronsRight, 'LastPageIcon');
export const CircleIcon = adminIcon(Circle, 'CircleIcon');
export const ErrorIcon = adminIcon(CircleAlert, 'ErrorIcon');
export const SuccessIcon = adminIcon(CircleCheck, 'SuccessIcon');
export const CircleDotIcon = adminIcon(CircleDot, 'CircleDotIcon');
export const FailureIcon = adminIcon(CircleX, 'FailureIcon');
export const ClipboardIcon = adminIcon(ClipboardList, 'ClipboardIcon');
export const ColumnsIcon = adminIcon(Columns3, 'ColumnsIcon');
export const DownloadIcon = adminIcon(Download, 'DownloadIcon');
export const ViewIcon = adminIcon(Eye, 'ViewIcon');
export const ViewOffIcon = adminIcon(EyeOff, 'ViewOffIcon');
export const FilterIcon = adminIcon(Filter, 'FilterIcon');
export const InfoIcon = adminIcon(Info, 'InfoIcon');
export const LockIcon = adminIcon(Lock, 'LockIcon');
export const ListChecksIcon = adminIcon(ListChecks, 'ListChecksIcon');
export const MailIcon = adminIcon(Mail, 'MailIcon');
export const MenuIcon = adminIcon(Menu, 'MenuIcon');
export const MinusIcon = adminIcon(Minus, 'MinusIcon');
export const EditIcon = adminIcon(Pencil, 'EditIcon');
export const AddIcon = adminIcon(Plus, 'AddIcon');
export const RepeatIcon = adminIcon(RefreshCw, 'RepeatIcon');
export const SearchIcon = adminIcon(Search, 'SearchIcon');
export const SettingsIcon = adminIcon(Settings, 'SettingsIcon');
export const ShapesIcon = adminIcon(Shapes, 'ShapesIcon');
export const ShieldIcon = adminIcon(ShieldCheck, 'ShieldIcon');
export const SlidersIcon = adminIcon(SlidersHorizontal, 'SlidersIcon');
export const SquareIcon = adminIcon(Square, 'SquareIcon');
export const SquareCheckIcon = adminIcon(SquareCheck, 'SquareCheckIcon');
export const SquareMinusIcon = adminIcon(SquareMinus, 'SquareMinusIcon');
export const TerminalIcon = adminIcon(Terminal, 'TerminalIcon');
export const DeleteIcon = adminIcon(Trash2, 'DeleteIcon');
export const WarningIcon = adminIcon(TriangleAlert, 'WarningIcon');
export const UsersIcon = adminIcon(Users, 'UsersIcon');
export const WrenchIcon = adminIcon(Wrench, 'WrenchIcon');
export const CloseIcon = adminIcon(X, 'CloseIcon');

// Official Lucide Lab coffeemaker (ISC license supplied by @lucide/lab).
const CoffeeMaker = forwardRef((props, ref) => <Icon iconNode={coffeemaker} {...props} ref={ref} />);
export const EspressoMachineIcon = adminIcon(CoffeeMaker, 'EspressoMachineIcon');

const alertIcons = { error: ErrorIcon, warning: WarningIcon, success: SuccessIcon, info: InfoIcon };
export function AdminAlertIcon({ status = 'error', ...props }) {
  const styles = useStyles();
  const StatusIcon = alertIcons[status] || InfoIcon;
  return (
    <Box as="span" display="inherit" __css={styles.icon} aria-hidden="true" {...props}>
      <StatusIcon width="100%" height="100%" />
    </Box>
  );
}
